import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { metaPageView } from "@/lib/metaPixel";
import { openaiPageView } from "@/lib/openaiPixel";

let injected = false;

/** Inject GA4, GTM, and legacy Meta Pixel script tags exactly once,
 *  driven by IDs returned from /api/settings. Empty IDs => skip.
 *  The new admin-driven Meta Pixel + CAPI (see lib/metaPixel.js) initialises
 *  itself independently and fires a route-change PageView from this hook. */
export const useAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    if (injected || typeof window === "undefined") return;
    injected = true;
    api
      .settings()
      .then((s) => {
        const { ga4, gtm, meta_pixel } = s.analytics || {};
        if (ga4) injectGA4(ga4);
        if (gtm) injectGTM(gtm);
        // Only inject the legacy pixel when NO admin-driven pixel exists,
        // otherwise the new lib/metaPixel.js will handle it (dual-track).
        if (meta_pixel && !window.fbq) injectMetaPixel(meta_pixel);
      })
      .catch(() => {});
  }, []);

  // Fire a PageView (browser Pixel + server CAPI, deduped by event_id)
  // on every route change once the Meta Pixel is configured. OpenAI's
  // oaiq pixel gets the same signal so ChatGPT Ads attribution stays
  // in sync.
  useEffect(() => {
    metaPageView();
    openaiPageView();
  }, [location.pathname, location.search]);
};

function injectGA4(id) {
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", id, { send_page_view: true });
}

function injectGTM(id) {
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s);
    const dl = l !== "dataLayer" ? `&l=${l}` : "";
    j.async = true;
    j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", id);
}

function injectMetaPixel(id) {
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", id);
  window.fbq("track", "PageView");
  /* eslint-enable */
}

export const trackEvent = (name, params = {}) => {
  try {
    if (window.gtag) window.gtag("event", name, params);
    if (window.fbq) window.fbq("trackCustom", name, params);
  } catch (_e) {
    /* noop */
  }
};
